/**
 * Documentary Management API Types
 *
 * Types for documentary list/detail and admin CRUD endpoints.
 * Mirrors backend Documentary* DTOs described in DOCUMENTARY_AND_THUMBNAIL_API_GUIDE.md.
 */

import type {
  VideoVariant,
  PreviewImageResponse,
} from './movie';

/**
 * Supported documentary types from backend.
 *
 * NOTE:
 * - Backend enum DocumentaryType uses UPPER_SNAKE_CASE values:
 *   DHARMA_SPEECH, ASTROLOGY, INTERVIEW, LITERARY_SEMINAR
 * - Always send these exact strings to the API.
 */
export type DocumentaryType =
  | 'DHARMA_SPEECH'
  | 'ASTROLOGY'
  | 'INTERVIEW'
  | 'LITERARY_SEMINAR'
  | string;

/**
 * Access level for a documentary.
 * Aligns with other content types (movies, music videos).
 */
export type DocumentaryAccessLevel = 'FREE' | 'PREMIUM' | 'ENTERPRISE';

/**
 * Content status for a documentary.
 */
export type DocumentaryStatus = 'ACTIVE' | 'INACTIVE' | 'PENDING';

/**
 * Documentary subtype for DHARMA_SPEECH type only.
 * 
 * NOTE:
 * - Only applicable when documentaryType = 'DHARMA_SPEECH'
 * - Optional (can be null for backward compatibility)
 * - Other types (ASTROLOGY, INTERVIEW, LITERARY_SEMINAR) do not use subtypes
 */
export type DocumentarySubtype =
  | 'WITH_MONK'      // Dharma Speech with monk
  | 'WITHOUT_MONK'   // Dharma Speech without monk
  | null;            // No subtype (for backward compatibility or non-DHARMA_SPEECH types)

/**
 * Core documentary response (summary rows from list endpoint).
 *
 * Matches backend DocumentaryResponse:
 * - contentId
 * - title
 * - description
 * - thumbnailUrl
 * - uploadDate
 * - languageId
 * - languageName
 * - accessLevel
 * - status
 * - documentaryType
 * - releaseDate
 * - topic
 * - expert
 * - duration
 * - averageRating
 * - ratingCount
 */
export interface Documentary {
  contentId: string;
  title: string;
  description?: string;
  thumbnailUrl?: string;
  uploadDate: string;
  languageId?: string;
  languageName?: string;
  accessLevel: DocumentaryAccessLevel;
  status: DocumentaryStatus;
  documentaryType: DocumentaryType;
  documentarySubtype?: DocumentarySubtype;
  releaseDate?: string;
  topic?: string;
  expert?: string;
  duration?: number | null;
  /**
   * Content priority for sorting (smaller number => higher priority).
   * Backend may return null/undefined for older rows.
   */
  priority?: number | null;
  averageRating?: number | null;
  ratingCount: number;
}

/**
 * Cast info attached to a documentary.
 * Mirrors backend CastDetailResponse.
 */
export interface DocumentaryCast {
  castId: string;
  name: string;
  nameMm?: string | null;
  profileImageUrl?: string | null;
  role?: string | null;
}

/**
 * Documentary detail response.
 *
 * Mirrors DocumentaryDetailResponse from backend:
 * - documentary
 * - casts
 * - videoVariants
 * - previewImages
 * - hasReadyVideos
 */
export interface DocumentaryDetailsResponse {
  documentary: Documentary;
  casts: DocumentaryCast[];
  videoVariants: VideoVariant[];
  previewImages: PreviewImageResponse[];
  hasReadyVideos: boolean;
}

/**
 * Paginated list data for documentaries.
 *
 * Backend wraps Page<DocumentaryResponse> in ApiResponse.
 */
export interface DocumentariesData {
  items: Documentary[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * Query params for listing documentaries.
 *
 * Backend:
 *  - page (1-based)
 *  - size
 *  - keyword
 *  - languageId
 *  - documentaryType
 */
export interface DocumentariesQueryParams {
  page?: number;
  size?: number;
  keyword?: string;
  languageId?: string;
  documentaryType?: DocumentaryType | string;
  documentarySubtype?: DocumentarySubtype | string;
  accessLevel?: string;
  /** Content region: "myanmar" | "international" */
  contentRegion?: string;
}

/**
 * Create Documentary Request payload (JSON body inside multipart `data`
 * field or JSON-only endpoint).
 */
export interface DocumentaryCreateRequest {
  title: string;
  description?: string;
  thumbnailUrl?: string;
  languageId: string;
  documentaryType: DocumentaryType;
  documentarySubtype?: DocumentarySubtype;
  releaseDate?: string;
  topic?: string;
  expert?: string;
  duration?: number;
}

/**
 * Update Documentary Request payload.
 *
 * All fields optional; mirrors backend DocumentaryUpdateRequest.
 */
export interface DocumentaryUpdateRequest {
  title?: string;
  description?: string;
  thumbnailUrl?: string;
  languageId?: string;
  documentaryType?: DocumentaryType;
  documentarySubtype?: DocumentarySubtype | null;
  releaseDate?: string;
  topic?: string;
  expert?: string;
  duration?: number;
  accessLevel?: DocumentaryAccessLevel;
  status?: DocumentaryStatus;
  /**
   * Optional priority update (admin).
   */
  priority?: number;
}

/**
 * Helper function to format date for display.
 */
export const formatDateForDisplay = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Helper function to format datetime for display.
 */
export const formatDateTimeForDisplay = (dateString: string | null | undefined): string => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Documentary subtype options for dropdowns.
 *
 * Labels are bilingual so admins can see the Burmese mobile-app section
 * name alongside the English description:
 *  - WITH_MONK    → appears in the "တရားတော်" section on the mobile app
 *  - WITHOUT_MONK → appears in the "သုတ္တန်" section on the mobile app
 */
export const DOCUMENTARY_SUBTYPE_OPTIONS: { value: DocumentarySubtype; label: string }[] = [
  { value: 'WITH_MONK', label: 'တရားတော် (With Monk)' },
  { value: 'WITHOUT_MONK', label: 'သုတ္တန် (Without Monk)' },
];

/**
 * Get a human-readable label for a documentary subtype value.
 * Returns an empty string for null/undefined so callers can render conditionally.
 */
export const getDocumentarySubtypeLabel = (
  subtype: DocumentarySubtype | null | undefined
): string => {
  if (!subtype) return '';
  const match = DOCUMENTARY_SUBTYPE_OPTIONS.find((option) => option.value === subtype);
  return match ? match.label : String(subtype).replace(/_/g, ' ');
};

/**
 * Check if subtype is applicable for a given documentary type
 */
export const isSubtypeApplicable = (documentaryType: DocumentaryType): boolean => {
  return documentaryType === 'DHARMA_SPEECH';
};


